import { useCallback, useEffect, useRef, useState } from 'react';
import { call, ApiError, type SignerSession } from './api';
import { loadPdfFromUrl, renderPage, type PdfDoc } from '../canvas/pdf';
import SignaturePad from './SignaturePad';
import { Markdown } from './markdown';
import { normalizedToScreen, validateValue, type Field } from '@beckersign/shared';

type Step = 'loading' | 'error' | 'auth' | 'disclosure' | 'review' | 'done' | 'declined';

/**
 * The signing experience.
 *
 * The step order is mandated by compliance-research.md § 5.2 and enforced by the
 * server; this UI follows the same sequence so the two never disagree. In
 * particular the document is not rendered until consent is recorded — the API
 * would refuse to serve it anyway, but showing a locked document would suggest
 * the disclosure is a formality standing between the signer and the real thing.
 */
export default function SigningApp({ token }: { token: string }) {
  const [step, setStep] = useState<Step>('loading');
  const [session, setSession] = useState<SignerSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState('');
  const [disclosure, setDisclosure] = useState<{ title: string; bodyMarkdown: string } | null>(null);
  const [agreed, setAgreed] = useState(false);          // never pre-checked — C-09, C-21
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [adopting, setAdopting] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [finishing, setFinishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await call<SignerSession>(token, 'session');
      setSession(s);
      setValues(s.values ?? {});
      setHasSignature(s.signature);
      if (s.recipient.status === 'completed') setStep('done');
      else if (s.recipient.status === 'declined') setStep('declined');
      else if (s.requiresAccessCode && !s.consented) setStep('auth');
      else if (!s.consented) setStep('disclosure');
      else setStep('review');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open this document.');
      setStep('error');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (step !== 'disclosure' || disclosure) return;
    void call<{ title: string; bodyMarkdown: string }>(token, 'disclosure')
      .then(setDisclosure)
      .catch(() => setError('Could not load the disclosure.'));
  }, [step, disclosure, token]);

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await call(token, 'auth', { code });
      setStep('disclosure');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not verify that code.');
    } finally { setBusy(false); }
  }

  async function giveConsent() {
    setBusy(true); setError(null);
    try {
      await call(token, 'consent', { agreed: true });
      await load();
      setStep('review');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record your consent.');
    } finally { setBusy(false); }
  }

  async function saveField(f: Field, value: string) {
    setValues((v) => ({ ...v, [f.id]: value }));
    const check = validateValue(value, f.validation ?? 'any', f.validationPattern);
    setFieldErrors((e) => ({ ...e, [f.id]: check.ok ? '' : (check.message ?? '') }));
    if (!check.ok || !value) return;
    try { await call(token, 'field', { fieldId: f.id, value }); }
    catch (e) { setFieldErrors((er) => ({ ...er, [f.id]: e instanceof ApiError ? e.message : 'Could not save.' })); }
  }

  async function adopt(method: string, png: string) {
    setBusy(true);
    try {
      await call(token, 'adopt', { method, imageDataUrl: png });
      setHasSignature(true); setAdopting(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not adopt your signature.');
    } finally { setBusy(false); }
  }

  async function finish() {
    setBusy(true); setError(null);
    try {
      await call(token, 'finish');
      setStep('done');
    } catch (e) {
      if (e instanceof ApiError && e.missingFieldIds?.length) {
        setError('Some required fields are still empty.');
        const first = document.getElementById(`field-${e.missingFieldIds[0]}`);
        first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (first as HTMLInputElement | null)?.focus();
      } else {
        setError(e instanceof ApiError ? e.message : 'Could not complete signing.');
      }
      setFinishing(false);
    } finally { setBusy(false); }
  }

  /** Lets a finished signer take a copy immediately, rather than only by email. */
  async function openMyCopy(documentId: string) {
    setError(null);
    try {
      const res = await call<{ url: string }>(token, 'document', { documentId });
      window.location.href = res.url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that document.');
    }
  }

  async function decline() {
    setBusy(true);
    try { await call(token, 'decline', { reason: declineReason }); setStep('declined'); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not record your decline.'); }
    finally { setBusy(false); }
  }

  // ── render ────────────────────────────────────────────────────────────────

  if (step === 'loading') return <Shell><p className="muted">Loading…</p></Shell>;

  if (step === 'error') return (
    <Shell>
      <h1>This link can’t be opened</h1>
      <div className="alert" role="alert">{error}</div>
      <p className="muted">If you think this is a mistake, reply to the email that brought you here.</p>
    </Shell>
  );

  if (step === 'done') return (
    <Shell>
      <h1>✓ You have signed</h1>
      <p>You signed <strong>{session?.envelopeName}</strong>.</p>
      <p className="muted">
        Once everyone has signed you’ll receive an email with a link to download the completed
        documents and the Certificate of Completion.
      </p>
      {session?.documents.map((d) => (
        <button
          key={d.id} style={{ width: '100%', marginBottom: '.5rem', minHeight: 48 }}
          onClick={() => void openMyCopy(d.id)}
        >
          Download {d.name}
        </button>
      ))}
      <p className="muted" style={{ fontSize: '.85rem', marginTop: '1rem' }}>
        If everyone has signed, this gives you the signed version. Otherwise it’s the document as
        you saw it. <strong>Save a copy somewhere you control.</strong>
      </p>
      {error && <div className="alert" role="alert">{error}</div>}
    </Shell>
  );

  if (step === 'declined') return (
    <Shell>
      <h1>You declined to sign</h1>
      <p className="muted">{session?.senderName} has been notified. You can close this window.</p>
    </Shell>
  );

  if (step === 'auth') return (
    <Shell>
      <h1>Verify it’s you</h1>
      <p className="muted">
        {session?.senderName} gave you a 6-character access code by phone, text or in person.
        It is not in the email.
      </p>
      <form onSubmit={submitCode}>
        <label>
          <span className="muted">Access code</span>
          <input
            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoCapitalize="characters" autoComplete="one-time-code" inputMode="text"
            maxLength={10} required aria-describedby={error ? 'code-err' : undefined}
            style={{ width: '100%', padding: '.7rem', minHeight: 52, fontSize: '1.4rem',
                     letterSpacing: '.25em', textAlign: 'center', fontFamily: 'ui-monospace, monospace' }}
          />
        </label>
        {error && <div className="alert" id="code-err" role="alert" style={{ marginTop: '.75rem' }}>{error}</div>}
        <button className="primary" style={{ marginTop: '1rem' }} disabled={busy || code.length < 4}>
          {busy ? 'Checking…' : 'Continue'}
        </button>
      </form>
      <DeclineLink onClick={() => setDeclining(true)} />
      {declining && <DeclineBox reason={declineReason} setReason={setDeclineReason} onCancel={() => setDeclining(false)} onConfirm={decline} busy={busy} />}
    </Shell>
  );

  if (step === 'disclosure') return (
    <Shell wide>
      <h1>Before you sign</h1>
      <p className="muted">
        Please read the Electronic Record and Signature Disclosure below. You’ll need to agree to it
        before you can view and sign <strong>{session?.envelopeName}</strong>.
      </p>
      <div style={{
        border: '1px solid var(--line)', borderRadius: 8, padding: '1rem 1.15rem',
        maxHeight: '55vh', overflowY: 'auto', background: 'var(--bg)',
      }}>
        {disclosure ? <Markdown source={disclosure.bodyMarkdown} /> : <p className="muted">Loading…</p>}
      </div>

      <label style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-start', marginTop: '1rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
               style={{ marginTop: '.2rem', width: 20, height: 20, flexShrink: 0 }} />
        <span>
          <strong>I have read the Electronic Record and Signature Disclosure, and I agree to conduct
          this transaction electronically and to use electronic records and electronic signatures.</strong>
        </span>
      </label>
      <p className="muted" style={{ fontSize: '.85rem', marginTop: '.5rem' }}>
        You may withdraw this consent at any time — see section 3 above.
      </p>

      {error && <div className="alert" role="alert">{error}</div>}
      <button className="primary" style={{ marginTop: '.75rem' }} disabled={!agreed || busy} onClick={() => void giveConsent()}>
        {busy ? 'Recording…' : 'Agree and Continue'}
      </button>
      <DeclineLink onClick={() => setDeclining(true)} />
      {declining && <DeclineBox reason={declineReason} setReason={setDeclineReason} onCancel={() => setDeclining(false)} onConfirm={decline} busy={busy} />}
    </Shell>
  );

  // ── review + fields + finish ──────────────────────────────────────────────
  const fields = session?.fields ?? [];
  const needsSignature = fields.some((f) => f.type === 'signature' || f.type === 'initials');

  /**
   * Signature and initials fields are NOT value-backed — they are satisfied by
   * adopting a signature, not by writing a fieldValue. Counting them here made
   * the progress total unreachable and left "Continue to sign" permanently
   * disabled, so the signer could never finish. Found by driving the deployed
   * flow in a browser; no unit test would have caught it.
   */
  const valueFields = fields.filter(
    (f) => f.required && !['date_signed', 'name', 'signature', 'initials'].includes(f.type)
  );
  const doneValues = valueFields.filter((f) => (values[f.id] ?? '').trim()).length;

  const totalSteps = valueFields.length + (needsSignature ? 1 : 0);
  const doneSteps = doneValues + (needsSignature && hasSignature ? 1 : 0);
  const allDone = doneSteps === totalSteps;

  if (adopting) return <Shell><SignaturePad defaultName={session?.recipient.name ?? ''} onAdopt={adopt} onCancel={() => setAdopting(false)} busy={busy} /></Shell>;

  if (finishing) return (
    <Shell>
      <h1>Finish and sign</h1>
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '1rem', margin: '1rem 0' }}>
        <div style={{ fontWeight: 600 }}>{session?.envelopeName}</div>
        <div className="muted" style={{ fontSize: '.85rem', marginTop: '.35rem' }}>
          {session?.documents.length} document{session?.documents.length === 1 ? '' : 's'} ·
          {' '}{valueFields.length} field{valueFields.length === 1 ? '' : 's'} completed
          {needsSignature && ' · signature adopted'}
        </div>
        <div className="muted" style={{ fontSize: '.85rem' }}>Sent by {session?.senderName}</div>
      </div>
      <p style={{ fontSize: '.98rem', lineHeight: 1.55 }}>
        <strong>When you select “Finish and Sign”, you are signing these documents. Your electronic
        signature will be applied, and this action cannot be undone.</strong>
      </p>
      <p style={{ fontSize: '.95rem' }}>
        By signing, you confirm that you have reviewed the documents, that the information you
        entered is accurate, and that you intend to be legally bound by them.
      </p>
      {error && <div className="alert" role="alert">{error}</div>}
      <button className="primary" style={{ marginTop: '.5rem', fontSize: '1.05rem', minHeight: 52 }}
              disabled={busy} onClick={() => void finish()}>
        {busy ? 'Signing…' : 'Finish and Sign'}
      </button>
      <div className="row" style={{ gap: '.5rem', marginTop: '.75rem', justifyContent: 'flex-start' }}>
        <button onClick={() => setFinishing(false)} disabled={busy}>Review documents again</button>
        <button onClick={() => setDeclining(true)} disabled={busy}>Decline to sign</button>
      </div>
      {declining && <DeclineBox reason={declineReason} setReason={setDeclineReason} onCancel={() => setDeclining(false)} onConfirm={decline} busy={busy} />}
    </Shell>
  );

  return (
    <Shell wide>
      <div className="row">
        <h1 style={{ margin: 0, fontSize: '1.2rem' }}>{session?.envelopeName}</h1>
        <span className="muted" style={{ fontSize: '.85rem' }}>
          {doneSteps} of {totalSteps} complete
        </span>
      </div>
      {session?.messageToSigners && (
        <p className="notice" style={{ fontStyle: 'italic' }}>{session.messageToSigners}</p>
      )}

      {session?.documents.map((d) => (
        <DocumentView key={d.id} token={token} doc={d} fields={fields}
                      values={values} errors={fieldErrors} onChange={saveField}
                      recipientName={session.recipient.name} hasSignature={hasSignature}
                      onAdopt={() => setAdopting(true)} />
      ))}

      {error && <div className="alert" role="alert">{error}</div>}

      <div style={{
        position: 'sticky', bottom: 0, background: 'var(--bg)', borderTop: '1px solid var(--line)',
        padding: '.85rem 0', marginTop: '1rem',
      }}>
        <button className="primary" style={{ minHeight: 52, fontSize: '1rem' }}
                disabled={!allDone} onClick={() => setFinishing(true)}>
          {allDone
            ? 'Continue to sign'
            : needsSignature && !hasSignature && doneValues === valueFields.length
              ? 'Adopt your signature to continue'
              : `${totalSteps - doneSteps} step${totalSteps - doneSteps === 1 ? '' : 's'} left`}
        </button>
        <DeclineLink onClick={() => setDeclining(true)} />
      </div>
      {declining && <DeclineBox reason={declineReason} setReason={setDeclineReason} onCancel={() => setDeclining(false)} onConfirm={decline} busy={busy} />}
    </Shell>
  );
}

function Shell({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <main className="wrap" style={{ maxWidth: wide ? '52rem' : '32rem' }}>
      <div className="muted" style={{ fontSize: '.75rem', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        BeckerSign
      </div>
      {children}
    </main>
  );
}

function DeclineLink({ onClick }: { onClick: () => void }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <button onClick={onClick} style={{ background: 'none', border: 'none', color: 'var(--muted)', textDecoration: 'underline', padding: '.5rem 0', minHeight: 44 }}>
        Decline to sign
      </button>
    </div>
  );
}

function DeclineBox({ reason, setReason, onCancel, onConfirm, busy }: {
  reason: string; setReason: (s: string) => void; onCancel: () => void; onConfirm: () => void; busy: boolean;
}) {
  return (
    <div style={{ border: '2px solid var(--danger)', borderRadius: 8, padding: '1rem', marginTop: '1rem' }}>
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Decline to sign</h2>
      <p className="muted" style={{ fontSize: '.88rem' }}>
        This will stop the transaction. The sender will be notified and nothing will be signed.
      </p>
      <label style={{ display: 'block' }}>
        <span className="muted" style={{ fontSize: '.85rem' }}>Reason (optional, but helpful)</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
                  style={{ width: '100%', padding: '.5rem' }} />
      </label>
      <div className="row" style={{ gap: '.5rem', marginTop: '.75rem', justifyContent: 'flex-start' }}>
        <button onClick={onConfirm} disabled={busy}
                style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
          {busy ? 'Declining…' : 'Decline to sign'}
        </button>
        <button onClick={onCancel} disabled={busy}>Go back</button>
      </div>
    </div>
  );
}

/** One document: rendered pages with this recipient's fields overlaid. */
function DocumentView({
  token, doc, fields, values, errors, onChange, recipientName, hasSignature, onAdopt,
}: {
  token: string;
  doc: { id: string; name: string; pageCount: number; pageSizes: { width: number; height: number }[] };
  fields: Field[]; values: Record<string, string>; errors: Record<string, string>;
  onChange: (f: Field, v: string) => void;
  recipientName: string; hasSignature: boolean; onAdopt: () => void;
}) {
  const [pdf, setPdf] = useState<PdfDoc | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void call<{ url: string }>(token, 'document', { documentId: doc.id })
      .then((r) => loadPdfFromUrl(r.url))
      .then((d) => { if (!cancelled) setPdf(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [token, doc.id]);

  if (failed) return <div className="alert">Could not load {doc.name}.</div>;
  if (!pdf) return <p className="muted">Loading {doc.name}…</p>;

  return (
    <div style={{ marginTop: '1rem' }}>
      {Array.from({ length: doc.pageCount }, (_, i) => (
        <Page key={i} pdf={pdf} pageIndex={i} token={token} documentId={doc.id}
              fields={fields.filter((f) => f.documentId === doc.id && f.pageIndex === i)}
              values={values} errors={errors} onChange={onChange}
              recipientName={recipientName} hasSignature={hasSignature} onAdopt={onAdopt} />
      ))}
    </div>
  );
}

function Page({
  pdf, pageIndex, token, documentId, fields, values, errors, onChange,
  recipientName, hasSignature, onAdopt,
}: {
  pdf: PdfDoc; pageIndex: number; token: string; documentId: string; fields: Field[];
  values: Record<string, string>; errors: Record<string, string>;
  onChange: (f: Field, v: string) => void;
  recipientName: string; hasSignature: boolean; onAdopt: () => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const seen = useRef(false);
  const enteredAt = useRef(0);

  useEffect(() => {
    if (!canvas.current || !wrap.current) return;
    const available = Math.min(wrap.current.clientWidth || 640, 900);
    void pdf.getPage(pageIndex + 1).then((p) => {
      const scale = available / p.getViewport({ scale: 1 }).width;
      return renderPage(pdf, pageIndex, canvas.current!, scale).then(setSize);
    });
  }, [pdf, pageIndex]);

  /**
   * Per-page view tracking. C-08 / compliance-research.md § 5.6 — this is what
   * rebuts "I never saw page 4".
   *
   * Records after a page has been CONTINUOUSLY VISIBLE for a dwell threshold,
   * not when it scrolls away. The first version fired only on exit, so a signer
   * who opened the document, signed, and never scrolled it out of view produced
   * no view events at all — which is the ordinary case, and left the log silent
   * on the one question it exists to answer. Found by reading the audit trail
   * after a real signing run.
   *
   * A page also flushes on exit if it left before reaching the threshold, so a
   * quick scroll past is still recorded with its true (short) dwell.
   */
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    let timer: number | undefined;

    const record = (dwellMs: number) => {
      if (seen.current) return;
      seen.current = true;
      void call(token, 'viewed', { documentId, pageIndex, dwellMs }).catch(() => undefined);
    };

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          enteredAt.current = Date.now();
          timer = window.setTimeout(() => record(Date.now() - enteredAt.current), 1500);
        } else {
          if (timer) { window.clearTimeout(timer); timer = undefined; }
          if (enteredAt.current) {
            const dwellMs = Date.now() - enteredAt.current;
            enteredAt.current = 0;
            if (dwellMs > 300) record(dwellMs);
          }
        }
      }
    }, { threshold: 0.4 });

    io.observe(el);
    return () => {
      if (timer) window.clearTimeout(timer);
      io.disconnect();
    };
  }, [token, documentId, pageIndex]);

  return (
    <div ref={wrap} style={{ position: 'relative', marginBottom: '1.25rem', lineHeight: 0 }}>
      <canvas ref={canvas} style={{ boxShadow: '0 1px 6px rgba(0,0,0,.18)', maxWidth: '100%' }} />
      {size.width > 0 && fields.map((f) => {
        const box = normalizedToScreen(f, size.width, size.height);
        const common = {
          position: 'absolute' as const, left: box.left, top: box.top,
          width: box.width, height: box.height, lineHeight: 'normal' as const,
        };
        const auto = f.type === 'name' ? recipientName
          : f.type === 'date_signed' ? new Date().toLocaleDateString()
          : null;

        if (auto !== null) return (
          <div key={f.id} style={{ ...common, display: 'flex', alignItems: 'center', padding: '0 4px',
            background: '#1f5fa911', border: '1px solid #1f5fa955', borderRadius: 3,
            fontSize: Math.max(9, Math.min(f.fontSize, box.height - 4)), color: '#111418' }}>
            {auto}
          </div>
        );

        if (f.type === 'signature' || f.type === 'initials') return (
          <button key={f.id} id={`field-${f.id}`} onClick={onAdopt} style={{
            ...common, background: hasSignature ? '#1c6b3f18' : '#a3801c22',
            border: `2px solid ${hasSignature ? '#1c6b3f' : '#a3801c'}`, borderRadius: 3,
            fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', padding: 0,
            color: hasSignature ? '#1c6b3f' : '#7a5f13',
          }}>
            {hasSignature
              ? (f.type === 'initials' ? '✓ Initialled' : '✓ Signed')
              : (f.type === 'initials' ? 'Tap to add initials' : 'Tap to sign')}
          </button>
        );

        if (f.type === 'checkbox') return (
          <input key={f.id} id={`field-${f.id}`} type="checkbox"
            checked={values[f.id] === 'true'}
            onChange={(e) => onChange(f, e.target.checked ? 'true' : '')}
            aria-label={f.label}
            style={{ ...common, width: Math.max(16, box.width), height: Math.max(16, box.height) }} />
        );

        return (
          <input key={f.id} id={`field-${f.id}`} type="text" value={values[f.id] ?? ''}
            placeholder={f.label} aria-label={f.label}
            aria-invalid={Boolean(errors[f.id])}
            onChange={(e) => onChange(f, e.target.value)}
            maxLength={f.maxLength ?? 500}
            style={{
              ...common, padding: '0 4px', borderRadius: 3,
              border: `2px solid ${errors[f.id] ? '#a3241c' : '#1f5fa9'}`,
              background: '#1f5fa90d',
              fontSize: Math.max(9, Math.min(f.fontSize, box.height - 4)),
            }} />
        );
      })}
    </div>
  );
}
